import { type CoreNewsAuthorInput } from "@/graphql/generated-types";
import { defineStore, type Store } from "pinia";
import type { StoreName, TabErrorSchemaTypes } from "@/types";
import type {
  IDefaultState,
  IDefultStoreGetters,
  IFileState,
} from "@/interfaces";
import md5 from "md5";
import helpers from "@/helpers";
import type { CoreNewsAuthorFragment } from "@/graphql";
import joi from "joi";
import toImageList from "@/helpers/files/toImageList";

export interface IAuthorsSate extends IDefaultState {
  item: IAuhtor;
  all: CoreNewsAuthorFragment[];
}

export type AuthorsStore = Store<
  string,
  IAuthorsSate,
  IDefultStoreGetters<IAuthorsSate>
>;

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  name: joi.string().max(50).required(),
  image: joi.array().min(1).required(),
});

type ValidationSchemaTypes = "update" | "create";

const VALIDATION_SCHEMA_MAP: Record<
  ValidationSchemaTypes,
  joi.ObjectSchema<any>
> = {
  update: VALIDATION_SCHEMA,
  create: VALIDATION_SCHEMA,
};

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["name", "title"],
  pictures: ["image"],
};

export interface IAuhtor {
  id: string | null;
  image?: string | null | undefined;
  name: string;
  status: boolean | null | undefined;
  title: string;
  imageList: IFileState[];
  __typename: string;
}

const DEFAULT_ITEM: IAuhtor = {
  id: null,
  title: "",
  name: "",
  status: true,
  image: null,
  imageList: [],
  __typename: "Authors",
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IAuthorsSate => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      all: [],
      item: {
        ...DEFAULT_ITEM,
      },
    }),
    actions: {
      currentHash() {
        const data = {
          ...this.getItem(),
        };

        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        // It can take times to TextEditorInput convert html to delta.
        // That's why there is a timeout here.
        const timeout = setTimeout(() => {
          this.originalHash = this.currentHash();
          clearTimeout(timeout);
        }, 10);
      },
      async setItem(item: CoreNewsAuthorFragment) {
        this.item = {
          ...DEFAULT_ITEM,
          title: item.title,
          name: item.name,
          id: item.id,
          imageList: toImageList(item),
          __typename: item.__typename as string,
          status: item.status,
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): CoreNewsAuthorInput {
        const item = { ...this.item };

        return {
          id: item.id?.toString(),
          name: item.name,
          status: item.status,
          title: item.title,
          image: item.image,
        };
      },
      async isNotValid(schema: ValidationSchemaTypes = "update") {
        const validationSchema = VALIDATION_SCHEMA_MAP[schema];
        const data = {
          ...this.item,
          image: this.item.imageList.filter((item) => item.action !== "delete"),
        };

        this.validation = helpers.forms.validate(validationSchema, data);

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
    },
  });
};

export const useAuthorsStore = createUseStore("AUTHORS");

// We use the same store structure for the create modal.
export const useAddAuthorsStore = createUseStore("ADD_AUTHORS");
